export const GA_MEASUREMENT_ID = 'G-JJEJHB0FVL';

type Gtag = (command: string, targetId: string | Date, params?: Record<string, any>) => void;

const getGtag = (): Gtag | null => {
  if (typeof window === 'undefined') return null;
  const g = (window as any).gtag as Gtag | undefined;
  return typeof g === 'function' ? g : null;
};

const getSourceDomain = (): string => {
  if (typeof document === 'undefined') return '(direct)';
  try {
    return document.referrer ? new URL(document.referrer).hostname.toLowerCase() : '(direct)';
  } catch {
    return '(direct)';
  }
};

const LLM_AGENT_PATTERNS: [RegExp, string][] = [
  [/GPTBot|ChatGPT-User|OAI-SearchBot/i, 'openai'],
  [/ClaudeBot|Claude-User|Claude-Web|anthropic-ai/i, 'anthropic'],
  [/PerplexityBot|Perplexity-User/i, 'perplexity'],
  [/Google-Extended|Gemini|GoogleOther/i, 'google'],
  [/Bytespider/i, 'bytedance'],
  [/CCBot/i, 'commoncrawl'],
  [/Applebot-Extended/i, 'apple'],
  [/meta-externalagent|FacebookBot/i, 'meta'],
  [/cohere-ai|YouBot|Diffbot|Amazonbot|MistralAI-User/i, 'other'],
  [/HeadlessChrome|Playwright|Puppeteer/i, 'headless_browser'],
];

const getLlmAgent = (): string | null => {
  if (typeof navigator === 'undefined') return null;
  const ua = navigator.userAgent || '';
  for (const [re, name] of LLM_AGENT_PATTERNS) {
    if (re.test(ua)) return name;
  }
  if ((navigator as any).webdriver) return 'webdriver';
  return null;
};

const getAgentParams = () => {
  const agent = getLlmAgent();
  return { is_llm_agent: agent !== null, llm_agent_name: agent || 'none' };
};

const getAnalyticsParams = (): Record<string, any> => ({
  source_domain: getSourceDomain(),
  ...getAgentParams(),
});

export const trackPageView = (path: string) => {
  const gtag = getGtag();
  if (!gtag) return;
  try {
    gtag('config', GA_MEASUREMENT_ID, {
      page_path: path,
      ...getAnalyticsParams(),
    });
  } catch {
    // noop
  }
};

export const trackEvent = (action: string, params?: Record<string, any>) => {
  const gtag = getGtag();
  if (!gtag) return;
  try {
    (gtag as any)('event', action, { ...getAnalyticsParams(), ...(params || {}) });
  } catch {
    // noop
  }
};

// Simple time-on-page tracker. Call startTimeOnPage when route displayed, and stop on hide.
let pageTimerStart: number | null = null;
let pageTimerPath: string | null = null;

export const startTimeOnPage = (path: string) => {
  pageTimerStart = performance.now();
  pageTimerPath = path;
};

export const stopAndSendTimeOnPage = () => {
  if (pageTimerStart == null || !pageTimerPath) return;
  const ms = Math.max(0, performance.now() - pageTimerStart);
  trackEvent('time_on_page', {
    page_path: pageTimerPath,
    value: Math.round(ms),
    engagement_time_msec: Math.round(ms),
  });
  pageTimerStart = null;
  pageTimerPath = null;
};

export const updateGtagConsent = (granted: boolean) => {
  const gtag = getGtag();
  if (!gtag) return;
  try {
    (gtag as any)('consent', 'update', {
      analytics_storage: granted ? 'granted' : 'denied',
      ad_storage: 'denied',
      ad_user_data: 'denied',
      ad_personalization: 'denied',
    });
  } catch {
    // noop
  }
};
